/**
 * Component: NotesRow
 * @description Displays notes for each day in a week, allowing users to add, edit, or delete notes. Manages employee and manager notes for daily tickets.
 *
 * @props
 *   @param {Array} daysOfWeek - Array of day objects, each containing a date and weekday information.
 *
 * @redux
 *   @selector dailyTickets - Retrieves daily tickets from the Redux store.
 *   @action updateDailyTicket - Dispatches an action to update a note in Firestore.
 *   @selector selectCurrentRole - Retrieves the current user's role.
 *   @selector selectHasPrivilegedRole - Determines if the user has manager/owner privileges.
 *
 * @state
 *   @state {boolean} modalOpen - Controls the visibility of the modal.
 *   @state {string|null} currentDay - Stores the selected day for editing notes.
 *   @state {string} noteText - The content of the current note being edited.
 *   @state {boolean} isManagerNote - Indicates if the note being edited is a manager note.
 *
 * @methods
 *   @function handleOpenModal - Opens the modal with the current note details for editing.
 *   @function handleSaveNote - Saves the updated note to Firestore.
 *   @function handleDeleteNote - Deletes the note from Firestore.
 *
 * @layout
 *   - StyledTableRow: Wrapper for the notes row in the table.
 *   - StyledShiftBox: Clickable box for each day, displaying the note content.
 *     Classes: 'single', 'first', 'last', 'current-day', 'selected' for styling variations.
 *   - Modal: Allows users to add, edit, or delete notes.
 *
 * @note
 *  !TODO: Manage styles when they are done to change the display of the current notes. Ask James and/or Sean.
 *
 * @example
 * <NotesRow daysOfWeek={weekDays} />
 */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography, Modal, Button, Tooltip } from '@mui/material';
import { formatDateToKey, formatLocalDate } from '../../../../utils/dateUtils';

import { selectDailyTickets } from '../../../../redux/dailyTickets/dailyTicketsSlice';
import { selectCurrentVenue } from '../../../../redux/slices/currentUserSlice';
import { updateDailyTicket } from '../../../../redux/dailyTickets/dailyTicketsThunks';
import { fetchDailyTickets } from '../../../../redux/dailyTickets/dailyTicketsThunks';
import { selectHasPrivilegedRole } from '../../../../redux/slices/currentUserSlice';

import {
  StyledShiftBox,
  StyledTableRow,
  StyledTableTitle,
  ActionButtons,
  NotesModal,
  OpenIcon,
  NoteContentBox,
  NoteContentWrapper,
  NotesTypography,
  IconsContainer,
  StyledTextField,
  NoteTypeIndicator,
} from './NotesRow.styles';

import EditIcon from '@mui/icons-material/Edit';
import NotesDetailView from './NotesDetailView';

const NotesRow = ({ daysOfWeek }) => {
  const dispatch = useDispatch();
  const dailyTickets = useSelector(selectDailyTickets);
  const selectedVenueId = useSelector(selectCurrentVenue).id;
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);

  useEffect(() => {
    dispatch(fetchDailyTickets(selectedVenueId));
  }, [dispatch, selectedVenueId]);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentDay, setCurrentDay] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [detailViewOpen, setDetailViewOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Create a map to quickly access tickets by dateKey (extracted from their ID)
  // Assuming the daily ticket IDs end with "_YYYY-MM-DD"
  const dailyTicketsMap = useMemo(() => {
    const map = {};
    dailyTickets.forEach((ticket) => {
      // ticket.id looks like: "gBCvRGkqCkZartTsDGeP_2024-12-13"
      const parts = ticket.id.split('_');
      if (parts.length === 2) {
        const ticketDate = parts[1]; // "2024-12-13"
        map[ticketDate] = ticket;
      }
    });
    return map;
  }, [dailyTickets]);

  const handleOpenModal = (dayKey) => {
    setCurrentDay(dayKey);
    const isManager = hasPrivilegedRole;
    const ticket = dailyTicketsMap[dayKey];
    // Show appropriate notes based on role
    setNoteText(isManager ? ticket?.managerNotes || '' : ticket?.employeeNotes || '');
    setModalOpen(true);
  };

  const handleSaveNote = () => {
    if (!currentDay) return;

    // Determine which field to update based on role
    const fieldName = hasPrivilegedRole ? 'managerNotes' : 'employeeNotes';
    const ticket = dailyTicketsMap[currentDay];

    if (ticket) {
      // Dispatch action to update Firestore
      dispatch(updateDailyTicket({ ticketId: ticket?.id, fieldName, noteText, vid: selectedVenueId }));
    }
    setModalOpen(false);
  };

  const handleDeleteNote = () => {
    if (!currentDay) return;

    const fieldName = hasPrivilegedRole ? 'managerNotes' : 'employeeNotes';
    const ticket = dailyTicketsMap[currentDay];
    if (ticket) {
      dispatch(updateDailyTicket({ ticketId: ticket?.id, fieldName, noteText: '', vid: selectedVenueId }));
    }

    setModalOpen(false);
  };

  const handleShowDetails = (ticket, dateKey) => {
    setSelectedTicket({ ticket, dateKey });
    setDetailViewOpen(true);
  };

  const getNoteTypeAndContent = useCallback((ticket) => {
    if (ticket?.managerNotes) {
      return { type: 'M', content: ticket.managerNotes, tooltip: 'Manager Note' };
    }
    if (ticket?.employeeNotes) {
      return { type: 'E', content: ticket.employeeNotes, tooltip: 'Employee Note' };
    }
    return { type: '-', content: '-', tooltip: 'No Notes' };
  }, []);

  return (
    <>
      <StyledTableRow>
        <StyledTableTitle component="th" scope="row">
          <Typography variant="footNoteRegular" color="#EBEBF599">
            Notes
          </Typography>
        </StyledTableTitle>
        {daysOfWeek.map((dayObj, index) => {
          const dateObj = new Date(dayObj.date);
          const dateKey = formatDateToKey(dateObj);

          // Find the ticket for this date
          const ticket = dailyTicketsMap[dateKey];
          const { type, content, tooltip } = getNoteTypeAndContent(ticket);

          // Determine position classes
          const positionClass =
            daysOfWeek.length === 1 ? 'single' : index === 0 ? 'first' : index === daysOfWeek.length - 1 ? 'last' : '';

          // Determine state classes
          const stateClasses = [
            positionClass,
            dayObj.isCurrentDay ? 'current-day' : '',
            currentDay === dateKey ? 'selected' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <StyledShiftBox
              key={dateKey}
              onClick={() => handleShowDetails(ticket, dateKey)}
              className={stateClasses}
              component="th"
              scope="col"
              tabIndex={0}
            >
              <NoteContentBox>
                <NoteContentWrapper>
                  <NotesTypography>
                    {content}
                  </NotesTypography>
                </NoteContentWrapper>
                <IconsContainer>
                  <OpenIcon
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenModal(dateKey);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </OpenIcon>
                  {type !== '-' && (
                    <Tooltip title={tooltip}>
                      <NoteTypeIndicator type={type}>{type}</NoteTypeIndicator>
                    </Tooltip>
                  )}

                </IconsContainer>
              </NoteContentBox>
            </StyledShiftBox>
          );
        })}
      </StyledTableRow>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <NotesModal>
          <Typography variant="h6" mb={2}>
            {currentDay ? `Edit ${hasPrivilegedRole ? 'Manager' : 'Employee'} Note for ${formatLocalDate(currentDay)}` : 'Add Note'}
          </Typography>
          <StyledTextField
            fullWidth
            multiline
            maxRows={4}
            variant="outlined"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={hasPrivilegedRole ? "Enter manager note" : "Enter employee note"}
          />
          <ActionButtons>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDeleteNote}
            >
              Delete
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveNote}
              disabled={!noteText.trim()}
            >
              Save
            </Button>
          </ActionButtons>
        </NotesModal>
      </Modal>
      {selectedTicket && (
        <NotesDetailView
          open={detailViewOpen}
          onClose={() => setDetailViewOpen(false)}
          managerNote={selectedTicket.ticket?.managerNotes}
          employeeNote={selectedTicket.ticket?.employeeNotes}
          date={selectedTicket.dateKey}
        />
      )}
    </>
  );
};

export default NotesRow;
